// ===========================================
// DetailContrat — Refonte UX (CRM layout : sidebar + KPIs + onglets)
// ===========================================
import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import {
    Card, Tabs, Table, Tag, Button, Typography, Dropdown,
    Modal, Form, Select, DatePicker, Input, Row, Col,
    Descriptions, Collapse, Alert,
} from 'antd';
import {
    FileProtectOutlined, DollarOutlined, WarningOutlined,
    EditOutlined, CarOutlined, CalendarOutlined, BankOutlined,
    SafetyCertificateOutlined, UserOutlined, ShopOutlined,
    PrinterOutlined, PlusOutlined, ReloadOutlined, ArrowLeftOutlined,
    DownloadOutlined, FilePdfOutlined, FileExcelOutlined, InfoCircleOutlined,
    ProfileOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';

import { contratsService, reglementsService } from '../../common/services';
import { useExporterName } from '../../common/hooks';
import { addExporterFooter } from '../../common/utils/pdfExport';
import { addExcelLogoHeader, addExcelExportInfo } from '../../common/utils/excelExport';
import { getContratStatutColor, getContratStatutLabel } from '../../common/constants';
import { formatMontant, formatDate } from '../../common/utils/format';
import DetailSkeleton from '../../components/detailSkeleton';
import MontantInput from '../../components/montantInput';
import PrintHeader from '../../components/printHeader';
import PrintFooter from '../../components/printFooter';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';

import './detail-contrat.page.scss';

const { Text } = Typography;

// ─── Format relatif (cohérent avec le reste de l'app) ───────────────────
const formatRelative = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 0) return formatDate(date);

    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffMin < 1)    return "À l'instant";
    if (diffMin < 60)   return `Il y a ${diffMin} min`;
    if (diffHr  < 24)   return `Il y a ${diffHr} h`;
    if (diffDay < 30)   return diffDay === 1 ? 'Hier' : `Il y a ${diffDay} jours`;
    if (diffMonth < 12) return `Il y a ${diffMonth} mois`;
    return `Il y a ${diffYear} an${diffYear > 1 ? 's' : ''}`;
};

const MOYEN_PAIEMENT_COLORS = {
    especes: 'green',
    cheque: 'blue',
    virement: 'purple',
    mobile_money: 'cyan',
};

const ECHEANCE_STATUT_COLORS = {
    paye: 'green',
    en_retard: 'red',
    en_attente: 'orange',
};

const DetailContrat = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const exporterName = useExporterName();
    const [contrat, setContrat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('informations');

    // Modal encaissement
    const [reglementModalVisible, setReglementModalVisible] = useState(false);
    const [reglementLoading, setReglementLoading] = useState(false);
    const [reglementForm] = Form.useForm();
    const reglementMontantRef = useRef(null);

    const fetchContrat = async () => {
        try {
            const res = await contratsService.getById(id);
            setContrat(res?.data || res);
        } catch (error) {
            toast.error('Erreur lors du chargement du contrat');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchContrat(); }, [id]);

    // Historique des versions (chaine de renouvellements)
    const [versions, setVersions] = useState([]);
    useEffect(() => {
        contratsService.getHistorique(id)
            .then((res) => setVersions(res?.data || res || []))
            .catch(() => setVersions([]));
    }, [id]);

    const openEncaisserModal = () => {
        const reste = Math.max(0, (contrat?.primeTTC || 0) - (contrat?.totalVerse || 0));
        reglementForm.setFieldsValue({
            montant: reste > 0 ? reste : undefined,
            dateReglement: dayjs(),
            moyenPaiement: 'especes',
        });
        setReglementModalVisible(true);
        setTimeout(() => {
            reglementMontantRef.current?.focus();
            reglementMontantRef.current?.select?.();
        }, 150);
    };

    const submitReglement = async (values) => {
        try {
            setReglementLoading(true);
            await reglementsService.create({
                ...values,
                contrat: contrat._id,
                client: contrat.client?._id,
                agence: contrat.agence?._id || contrat.agence,
            });
            toast.success('Règlement enregistré avec succès');
            setReglementModalVisible(false);
            reglementForm.resetFields();
            setLoading(true);
            fetchContrat();
        } catch (error) {
            toast.error(error.message || "Erreur lors de l'enregistrement");
        } finally {
            setReglementLoading(false);
        }
    };

    const handleAddReglement = async (values) => {
        const reste = contrat?.resteAPayer ?? 0;
        const montant = values?.montant || 0;
        if (reste > 0 && montant > reste) {
            const surplus = montant - reste;
            Modal.confirm({
                title: 'Montant supérieur au reste à payer',
                content: (
                    <div>
                        <p>Vous saisissez <strong>{formatMontant(montant)}</strong> alors qu'il ne reste que <strong>{formatMontant(reste)}</strong> à payer sur ce contrat.</p>
                        <p>Cela va générer un <strong>trop-perçu de {formatMontant(surplus)}</strong>. Voulez-vous continuer ?</p>
                    </div>
                ),
                okText: 'Oui, enregistrer quand même',
                cancelText: 'Annuler',
                okButtonProps: { danger: true },
                onOk: () => submitReglement(values),
            });
            return;
        }
        return submitReglement(values);
    };

    if (loading) return <DetailSkeleton kpis={4} sections={2} />;

    if (!contrat) {
        return (
            <div className="detail-contrat-empty">
                <Text type="secondary">Contrat introuvable</Text>
            </div>
        );
    }

    const clientNom = contrat.client?.raisonSociale
        || `${contrat.client?.nom || ''} ${contrat.client?.prenom || ''}`.trim()
        || '—';

    const hasVehicule = contrat.immatriculation || contrat.marqueVehicule || contrat.energie;
    const reste = contrat.resteAPayer ?? Math.max(0, (contrat.primeTTC || 0) - (contrat.totalVerse || 0));
    const accCourtier = contrat.accessoiresCourtier != null
        ? contrat.accessoiresCourtier
        : (contrat.accessoiresAssureur != null ? 0 : (contrat.accessoires || 0));
    const accAssureur = contrat.accessoiresAssureur || 0;

    // ─── Colonnes Règlements ──────────────────────────────────────────
    const reglementColumns = [
        {
            title: 'Date',
            dataIndex: 'dateReglement',
            key: 'date',
            width: 110,
            render: (d, r) => {
                const date = d || r.createdAt;
                return date ? formatDate(date) : '—';
            },
        },
        {
            title: 'Montant',
            dataIndex: 'montant',
            key: 'montant',
            align: 'right',
            width: 140,
            render: (v) => <span className="detail-amount detail-amount-positive">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Mode',
            dataIndex: 'moyenPaiement',
            key: 'mode',
            width: 130,
            render: (m) => <Tag color={MOYEN_PAIEMENT_COLORS[m] || 'default'}>{m || '—'}</Tag>,
        },
        {
            title: 'Référence',
            dataIndex: 'reference',
            key: 'reference',
            render: (t) => t || '—',
        },
    ];

    // ─── Colonnes Échéancier ──────────────────────────────────────────
    const echeancierColumns = [
        { title: 'N°', dataIndex: 'numero', key: 'numero', width: 60, align: 'center' },
        {
            title: "Date d'échéance",
            dataIndex: 'dateEcheance',
            key: 'date',
            width: 140,
            render: (d) => formatDate(d),
        },
        {
            title: 'Montant',
            dataIndex: 'montant',
            key: 'montant',
            align: 'right',
            width: 150,
            render: (v) => <span className="detail-amount">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            width: 130,
            render: (s) => <Tag color={ECHEANCE_STATUT_COLORS[s] || 'default'}>{s || '—'}</Tag>,
        },
    ];

    // ─── Exports PDF / Excel (logique préservée) ──────────────────────
    const fmtMontantNum = (v) => String(v || 0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    const handleExportPDF = () => {
        const doc = new jsPDF();
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;
        const ml = 14;
        const mr = pageWidth - 14;
        const contentW = mr - ml;

        doc.addImage(logoCap, 'JPEG', ml, 8, 40, 16);
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, mr, 15, { align: 'right' });

        const agenceNom = contrat.agence?.nom || '';
        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        doc.text(`AGENCE : ${agenceNom}`, centerX, 15, { align: 'center' });

        doc.setFontSize(14);
        doc.setFont(undefined, 'bolditalic');
        doc.text("FICHE CONTRAT D'ASSURANCE", centerX, 32, { align: 'center' });
        doc.setFont(undefined, 'normal');

        doc.setDrawColor(26, 43, 95);
        doc.setLineWidth(0.8);
        doc.line(ml, 35, mr, 35);

        let y = 42;
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(26, 43, 95);
        doc.text('SOUSCRIPTEUR & CONTRAT', ml, y);
        y += 6;

        autoTable(doc, {
            startY: y,
            body: [
                ['Souscripteur', clientNom, 'N° Police', contrat.numeroPolice || 'N/A'],
                ['Compagnie', contrat.compagnie?.nom || 'N/A', 'N. Attestation', contrat.numeroAttestation || 'N/A'],
                ["Type d'assurance", contrat.typeAssurance?.nom || 'N/A', 'Agence', contrat.agence?.nom || 'N/A'],
                ['Statut', (contrat.statut || '').toUpperCase(), 'Durée', `${contrat.duree || 12} mois`],
                ["Date d'effet", formatDate(contrat.dateEffet), "Date d'échéance", formatDate(contrat.dateEcheance)],
            ],
            theme: 'grid',
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: {
                0: { fontStyle: 'bold', fillColor: [245, 247, 250], textColor: [100, 100, 100], cellWidth: 38 },
                1: { fontStyle: 'bold' },
                2: { fontStyle: 'bold', fillColor: [245, 247, 250], textColor: [100, 100, 100], cellWidth: 38 },
                3: { fontStyle: 'bold' },
            },
            margin: { left: ml, bottom: 35 },
        });

        y = doc.lastAutoTable.finalY + 8;

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(26, 43, 95);
        doc.text('DÉCOMPOSITION DE LA PRIME', ml, y);
        y += 4;

        autoTable(doc, {
            startY: y,
            head: [['Élément', 'Montant (FCFA)']],
            body: [
                ['Prime nette', fmtMontantNum(contrat.primeNette)],
                ['Taxes', fmtMontantNum(contrat.taxes)],
                ['FGA', fmtMontantNum(contrat.fga)],
                ['CEDEAO', fmtMontantNum(contrat.cedeao)],
                ['Accessoires courtier', fmtMontantNum(accCourtier)],
                ['Accessoires assureur', fmtMontantNum(accAssureur)],
                [`Commission courtier (${contrat.tauxCommission || 0}%)`, fmtMontantNum(contrat.commissionCourtier)],
            ],
            foot: [[
                { content: 'PRIME TTC', styles: { fontStyle: 'bold', fillColor: [26, 43, 95], textColor: 255 } },
                { content: fmtMontantNum(contrat.primeTTC), styles: { halign: 'right', fontStyle: 'bold', fillColor: [26, 43, 95], textColor: 255 } },
            ]],
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { fillColor: [200, 16, 46], textColor: 255, fontStyle: 'bold' },
            columnStyles: { 1: { halign: 'right' } },
            alternateRowStyles: { fillColor: [245, 247, 250] },
            tableWidth: contentW * 0.65,
            margin: { left: ml, bottom: 35 },
        });

        y = doc.lastAutoTable.finalY + 8;
        if (hasVehicule) {
            if (y > 220) { doc.addPage(); y = 20; }
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(26, 43, 95);
            doc.text('INFORMATIONS VÉHICULE', ml, y);
            y += 4;
            const vehicule = [];
            if (contrat.immatriculation) vehicule.push(['Immatriculation', contrat.immatriculation]);
            if (contrat.marqueVehicule) vehicule.push(['Marque / Modèle', contrat.marqueVehicule]);
            if (contrat.categorieRisque) vehicule.push(['Catégorie risque', contrat.categorieRisque]);
            if (contrat.energie) vehicule.push(['Énergie', contrat.energie]);
            if (contrat.categorieVehicule) vehicule.push(['Catégorie véhicule', contrat.categorieVehicule]);
            if (contrat.usageVehicule) vehicule.push(['Usage', contrat.usageVehicule]);
            autoTable(doc, {
                startY: y,
                head: [['Élément', 'Détail']],
                body: vehicule,
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: { fillColor: [26, 43, 95], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [245, 247, 250] },
                tableWidth: contentW * 0.65,
                margin: { left: ml, bottom: 35 },
            });
            y = doc.lastAutoTable.finalY + 8;
        }

        if (contrat.reglements?.length > 0) {
            if (y > 220) { doc.addPage(); y = 20; }
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(26, 43, 95);
            doc.text('HISTORIQUE DES RÈGLEMENTS', ml, y);
            y += 4;
            autoTable(doc, {
                startY: y,
                head: [['Date', 'Montant (FCFA)', 'Mode', 'Référence']],
                body: contrat.reglements.map((r) => {
                    const d = r.dateReglement || r.createdAt;
                    return [
                        d ? new Date(d).toLocaleDateString('fr-FR') : '—',
                        fmtMontantNum(r.montant),
                        r.moyenPaiement || '',
                        r.reference || '',
                    ];
                }),
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: { fillColor: [82, 196, 26], textColor: 255, fontStyle: 'bold' },
                columnStyles: { 1: { halign: 'right' } },
                alternateRowStyles: { fillColor: [245, 247, 250] },
                margin: { left: ml, right: 14, bottom: 35 },
            });
        }

        const pageCount = doc.internal.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.height;
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(1);
            doc.line(ml, pageHeight - 28, mr, pageHeight - 28);
            doc.setFontSize(8);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text(
                'Cocody 2 Plateaux Vallon ; Rue des Jardins, 28 BP 56 Abidjan 28 / Tel: (+225) 07 10 01 60 54 / 07 88 47 17 79 / 27 22 41 93 50',
                pageWidth / 2, pageHeight - 20, { align: 'center' },
            );
            doc.setFont(undefined, 'normal');
            doc.setFontSize(7);
            doc.text(
                'RCCM: CI-ABJ-03-2024-B13-023771 | NCC: 2400889R | infos@capassurances.com',
                pageWidth / 2, pageHeight - 14, { align: 'center' },
            );
        }

        addExporterFooter(doc, exporterName);
        doc.save(`contrat_${contrat.numeroPolice}_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    const handleExportExcel = async () => {
        const today = new Date().toLocaleDateString('fr-FR');
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Contrat');
        ws.columns = [{ width: 30 }, { width: 25 }, { width: 25 }, { width: 20 }];
        await addExcelLogoHeader(wb, ws);

        const titleRow = ws.addRow([`FICHE CONTRAT - ${contrat.numeroPolice}`]);
        ws.mergeCells(titleRow.number, 1, titleRow.number, 4);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };
        addExcelExportInfo(ws, { colCount: 4, exporterName, today });
        ws.addRow([]);

        const addSection = (title) => {
            const row = ws.addRow([title]);
            ws.mergeCells(ws.rowCount, 1, ws.rowCount, 4);
            row.getCell(1).font = { bold: true, size: 12, color: { argb: '1A2B5F' } };
            row.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E8ECF3' } };
            row.height = 22;
        };
        const addRow = (label, value) => {
            const row = ws.addRow([label, value]);
            row.getCell(1).font = { color: { argb: '666666' }, size: 10 };
            row.getCell(2).font = { bold: true, size: 10 };
        };

        addSection('SOUSCRIPTEUR & CONTRAT');
        addRow('Souscripteur', clientNom);
        addRow('Compagnie', contrat.compagnie?.nom || 'N/A');
        addRow("Type d'assurance", contrat.typeAssurance?.nom || 'N/A');
        addRow('N° Police', contrat.numeroPolice || '');
        addRow('N. Attestation', contrat.numeroAttestation || 'N/A');
        addRow('Agence', contrat.agence?.nom || 'N/A');
        addRow('Statut', (contrat.statut || '').toUpperCase());
        ws.addRow([]);
        addSection('PÉRIODE');
        addRow("Date d'effet", formatDate(contrat.dateEffet));
        addRow("Date d'échéance", formatDate(contrat.dateEcheance));
        addRow('Durée', `${contrat.duree || 12} mois`);
        ws.addRow([]);
        addSection('DÉCOMPOSITION DE LA PRIME');
        addRow('Prime nette', fmtMontantNum(contrat.primeNette));
        addRow('Taxes', fmtMontantNum(contrat.taxes));
        addRow('FGA', fmtMontantNum(contrat.fga));
        addRow('CEDEAO', fmtMontantNum(contrat.cedeao));
        addRow('Acc. courtier', fmtMontantNum(accCourtier));
        addRow('Acc. assureur', fmtMontantNum(accAssureur));
        addRow(`Commission (${contrat.tauxCommission || 0}%)`, fmtMontantNum(contrat.commissionCourtier));
        const ttc = ws.addRow(['PRIME TTC', fmtMontantNum(contrat.primeTTC)]);
        ttc.getCell(1).font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
        ttc.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1A2B5F' } };
        ttc.getCell(2).font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
        ttc.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1A2B5F' } };
        ttc.getCell(2).alignment = { horizontal: 'right' };

        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `contrat_${contrat.numeroPolice}_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    // ─── Onglets ──────────────────────────────────────────────────────
    const nbReglements = contrat.reglements?.length || 0;
    const nbEcheances = contrat.echeancier?.length || 0;

    const tabItems = [
        {
            key: 'informations',
            label: (
                <span className="tab-label">
                    <ProfileOutlined />
                    <span className="tab-label-text">Informations</span>
                </span>
            ),
            children: (
                <div className="detail-contrat-info">
                    {/* Souscripteur & Contrat */}
                    <div className="info-section">
                        <div className="info-section-title">
                            <UserOutlined /> Souscripteur & Contrat
                        </div>
                        <div className="info-grid">
                            <div className="info-cell">
                                <div className="info-label">Souscripteur</div>
                                <div className="info-value info-value-strong">{clientNom}</div>
                            </div>
                            <div className="info-cell">
                                <div className="info-label">Compagnie</div>
                                <div className="info-value">{contrat.compagnie?.nom || '—'}</div>
                            </div>
                            <div className="info-cell">
                                <div className="info-label">Type d'assurance</div>
                                <div className="info-value">
                                    <Tag color="blue">{contrat.typeAssurance?.nom || '—'}</Tag>
                                </div>
                            </div>
                            <div className="info-cell">
                                <div className="info-label">N° Police</div>
                                <div className="info-value detail-mono">{contrat.numeroPolice || '—'}</div>
                            </div>
                            <div className="info-cell">
                                <div className="info-label">N. Attestation</div>
                                <div className="info-value">{contrat.numeroAttestation || '—'}</div>
                            </div>
                            <div className="info-cell">
                                <div className="info-label">Agence</div>
                                <div className="info-value">{contrat.agence?.nom || '—'}</div>
                            </div>
                        </div>
                    </div>

                    {/* Période */}
                    <div className="info-section">
                        <div className="info-section-title">
                            <CalendarOutlined /> Période du contrat
                        </div>
                        <div className="info-period">
                            <div className="info-period-item">
                                <div className="info-label">Date d'effet</div>
                                <div className="info-period-value info-period-effet">
                                    {formatDate(contrat.dateEffet)}
                                </div>
                            </div>
                            <div className="info-period-item">
                                <div className="info-label">Date d'échéance</div>
                                <div className="info-period-value info-period-echeance">
                                    {formatDate(contrat.dateEcheance)}
                                </div>
                            </div>
                            <div className="info-period-item">
                                <div className="info-label">Durée</div>
                                <div className="info-period-value">{contrat.duree || 12} mois</div>
                            </div>
                        </div>
                    </div>

                    {/* Décomposition de la prime */}
                    <div className="info-section">
                        <div className="info-section-title">
                            <DollarOutlined /> Décomposition de la prime
                        </div>
                        <div className="info-prime-grid">
                            <div className="prime-line"><span>Prime nette</span><span className="detail-amount">{formatMontant(contrat.primeNette)}</span></div>
                            <div className="prime-line"><span>Taxes</span><span className="detail-amount">{formatMontant(contrat.taxes)}</span></div>
                            <div className="prime-line"><span>FGA</span><span className="detail-amount">{formatMontant(contrat.fga)}</span></div>
                            <div className="prime-line"><span>CEDEAO</span><span className="detail-amount">{formatMontant(contrat.cedeao)}</span></div>
                            <div className="prime-line"><span>Accessoires courtier</span><span className="detail-amount">{formatMontant(accCourtier)}</span></div>
                            <div className="prime-line"><span>Accessoires assureur</span><span className="detail-amount">{formatMontant(accAssureur)}</span></div>
                            <div className="prime-line"><span>Commission ({contrat.tauxCommission || 0}%)</span><span className="detail-amount">{formatMontant(contrat.commissionCourtier)}</span></div>
                            <div className="prime-total">
                                <span>PRIME TTC</span>
                                <span>{formatMontant(contrat.primeTTC)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Véhicule (si auto) */}
                    {hasVehicule && (
                        <div className="info-section">
                            <div className="info-section-title">
                                <CarOutlined /> Informations véhicule
                            </div>
                            <div className="info-grid">
                                {contrat.immatriculation && (
                                    <div className="info-cell">
                                        <div className="info-label">Immatriculation</div>
                                        <div className="info-value detail-mono">{contrat.immatriculation}</div>
                                    </div>
                                )}
                                {contrat.marqueVehicule && (
                                    <div className="info-cell">
                                        <div className="info-label">Marque / Modèle</div>
                                        <div className="info-value info-value-strong">{contrat.marqueVehicule}</div>
                                    </div>
                                )}
                                {contrat.categorieRisque && (
                                    <div className="info-cell">
                                        <div className="info-label">Catégorie risque</div>
                                        <div className="info-value">
                                            <Tag color="orange">{contrat.categorieRisque}</Tag>
                                        </div>
                                    </div>
                                )}
                                {contrat.energie && (
                                    <div className="info-cell">
                                        <div className="info-label">Énergie</div>
                                        <div className="info-value">
                                            <Tag color="gold">{contrat.energie}</Tag>
                                        </div>
                                    </div>
                                )}
                                {contrat.categorieVehicule && (
                                    <div className="info-cell">
                                        <div className="info-label">Catégorie véhicule</div>
                                        <div className="info-value">{contrat.categorieVehicule}</div>
                                    </div>
                                )}
                                {contrat.usageVehicule && (
                                    <div className="info-cell">
                                        <div className="info-label">Usage / Zone</div>
                                        <div className="info-value">{contrat.usageVehicule}</div>
                                    </div>
                                )}
                                {contrat.conducteurHabituel && (
                                    <div className="info-cell">
                                        <div className="info-label">Conducteur</div>
                                        <div className="info-value">{contrat.conducteurHabituel}</div>
                                    </div>
                                )}
                                {contrat.classeConduteur && (
                                    <div className="info-cell">
                                        <div className="info-label">Classe conducteur</div>
                                        <div className="info-value">{contrat.classeConduteur}</div>
                                    </div>
                                )}
                                {contrat.numeroCedeao && (
                                    <div className="info-cell">
                                        <div className="info-label">N. CEDEAO</div>
                                        <div className="info-value detail-mono">{contrat.numeroCedeao}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: 'reglements',
            label: (
                <span className="tab-label">
                    <DollarOutlined />
                    <span className="tab-label-text">Règlements</span>
                    <span className="detail-tab-count">{nbReglements}</span>
                </span>
            ),
            children: (
                <div className="detail-contrat-reglements">
                    {reste > 0 && contrat.statut === 'actif' && (
                        <div className="reglements-cta">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openEncaisserModal}
                                className="sidebar-btn-cta"
                            >
                                Encaisser ({formatMontant(reste)})
                            </Button>
                        </div>
                    )}
                    <Table
                        dataSource={contrat.reglements || []}
                        columns={reglementColumns}
                        rowKey="_id"
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: 'Aucun règlement' }}
                        pagination={{ pageSize: 10, showTotal: (t) => `${t} règlement${t > 1 ? 's' : ''}` }}
                        summary={(data) => {
                            if (!data.length) return null;
                            const total = data.reduce((s, r) => s + (r.montant || 0), 0);
                            return (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">
                                        <strong className="detail-amount detail-amount-positive">{formatMontant(total)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} colSpan={2} />
                                </Table.Summary.Row>
                            );
                        }}
                    />
                </div>
            ),
        },
        {
            key: 'echeancier',
            label: (
                <span className="tab-label">
                    <CalendarOutlined />
                    <span className="tab-label-text">Échéancier</span>
                    {nbEcheances > 0 && <span className="detail-tab-count">{nbEcheances}</span>}
                </span>
            ),
            children: (
                <Table
                    dataSource={contrat.echeancier || []}
                    columns={echeancierColumns}
                    rowKey={(r) => r.numero || r._id}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'Aucun échéancier' }}
                    pagination={false}
                />
            ),
        },
        // Onglet Historique : visible uniquement si le contrat fait partie d'une
        // chaine de renouvellements (au moins 2 versions).
        ...(versions.length > 1 ? [{
            key: 'historique',
            label: (
                <span className="tab-label">
                    <ReloadOutlined />
                    <span className="tab-label-text">Historique</span>
                    <span className="detail-tab-count">{versions.length}</span>
                </span>
            ),
            children: (
                <Table
                    dataSource={versions}
                    rowKey="_id"
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    columns={[
                        {
                            title: 'Version',
                            dataIndex: 'numeroVersion',
                            key: 'version',
                            width: 130,
                            render: (v, r) => (
                                <span>
                                    <Tag color="blue">v{v || 1}</Tag>
                                    {String(r._id) === String(contrat._id) && <Tag color="green">Courante</Tag>}
                                </span>
                            ),
                        },
                        { title: 'N° Police', dataIndex: 'numeroPolice', key: 'numeroPolice' },
                        { title: 'Compagnie', key: 'compagnie', render: (_, r) => r.compagnie?.nom || '—' },
                        {
                            title: 'Période',
                            key: 'periode',
                            render: (_, r) => `${formatDate(r.dateEffet)} → ${formatDate(r.dateEcheance)}`,
                        },
                        {
                            title: 'Prime TTC',
                            dataIndex: 'primeTTC',
                            key: 'primeTTC',
                            align: 'right',
                            render: (v) => formatMontant(v || 0),
                        },
                        {
                            title: 'Statut',
                            dataIndex: 'statut',
                            key: 'statut',
                            render: (s) => <Tag color={getContratStatutColor(s)}>{getContratStatutLabel(s)}</Tag>,
                        },
                        {
                            title: '',
                            key: 'action',
                            width: 90,
                            render: (_, r) => (
                                String(r._id) === String(contrat._id)
                                    ? null
                                    : <Button type="link" size="small" onClick={() => navigate(`/dashboard/contrat/${r._id}`)}>Ouvrir</Button>
                            ),
                        },
                    ]}
                />
            ),
        }] : []),
    ];

    const exportMenuItems = [
        { key: 'pdf', label: 'Exporter en PDF', icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#389e0d' }} />, onClick: handleExportExcel },
    ];

    return (
        <div className="detail-contrat-page">
            <PrintHeader
                title={`Détail du contrat ${contrat.numeroPolice || ''}`}
                subtitle={`Client : ${clientNom} — Compagnie : ${contrat.compagnie?.nom || ''}`}
                agenceName={contrat.agence?.nom}
            />

            {/* Layout CRM : sidebar contrat (gauche) | content (droite) */}
            <div className="detail-contrat-layout">
                {/* ═════ SIDEBAR ═════ */}
                <div className="detail-contrat-sidebar no-print">
                    <Card className="detail-contrat-sidebar-card">
                        {/* Retour à la liste */}
                        <button
                            type="button"
                            className="sidebar-back-btn"
                            onClick={() => navigate('/dashboard/contrats')}
                            aria-label="Retour à la liste des contrats"
                        >
                            <ArrowLeftOutlined />
                            <span>Retour à la liste des contrats</span>
                        </button>

                        {/* Identité du contrat */}
                        <div className="sidebar-identity">
                            <div className="sidebar-contrat-icon">
                                <FileProtectOutlined />
                            </div>
                            <h1 className="sidebar-name">N° {contrat.numeroPolice || '—'}</h1>
                            <div className="sidebar-meta">
                                <Tag color={getContratStatutColor(contrat.statut)}>
                                    {getContratStatutLabel(contrat.statut)?.toUpperCase() || '—'}
                                </Tag>
                                {contrat.typeAssurance?.nom && (
                                    <Tag color="blue">{contrat.typeAssurance.nom}</Tag>
                                )}
                                {contrat.numeroVersion > 1 && (
                                    <Tag color="purple" icon={<ReloadOutlined />}>Renouvellement v{contrat.numeroVersion}</Tag>
                                )}
                            </div>
                            <div className="sidebar-since">
                                <CalendarOutlined /> Créé {formatRelative(contrat.createdAt)}
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        {/* Coordonnées contrat */}
                        <div className="sidebar-coord">
                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><UserOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Souscripteur</div>
                                    <div className="sidebar-coord-value">
                                        {contrat.client?._id ? (
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/dashboard/client/${contrat.client._id}`)}
                                                className="sidebar-coord-link sidebar-coord-link-btn"
                                            >
                                                {clientNom}
                                            </button>
                                        ) : clientNom}
                                    </div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><BankOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Compagnie</div>
                                    <div className="sidebar-coord-value">{contrat.compagnie?.nom || '—'}</div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><ShopOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Agence</div>
                                    <div className="sidebar-coord-value">{contrat.agence?.nom || '—'}</div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><SafetyCertificateOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">N. Attestation</div>
                                    <div className="sidebar-coord-value">{contrat.numeroAttestation || '—'}</div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><CalendarOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Échéance</div>
                                    <div className="sidebar-coord-value">{formatDate(contrat.dateEcheance)}</div>
                                </div>
                            </div>
                        </div>

                    </Card>
                </div>

                {/* ═════ MAIN ═════ */}
                <div className="detail-contrat-main">
                    {/* KPIs */}
                    <section className="detail-contrat-kpis no-print" aria-label="Indicateurs contrat">
                        <div className="detail-kpi detail-kpi-blue">
                            <div className="detail-kpi-icon"><DollarOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Prime TTC</div>
                                <div className="detail-kpi-value">{formatMontant(contrat.primeTTC)}</div>
                                <div className="detail-kpi-sub">engagement</div>
                            </div>
                        </div>

                        <div className="detail-kpi detail-kpi-green">
                            <div className="detail-kpi-icon"><DollarOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Total versé</div>
                                <div className="detail-kpi-value">{formatMontant(contrat.totalVerse)}</div>
                                <div className="detail-kpi-sub">
                                    {contrat.primeTTC > 0
                                        ? `${Math.round(((contrat.totalVerse || 0) / contrat.primeTTC) * 100)}% payé`
                                        : '—'}
                                </div>
                            </div>
                        </div>

                        <div className={`detail-kpi ${reste > 0 ? 'detail-kpi-red' : 'detail-kpi-green'}`}>
                            <div className="detail-kpi-icon">
                                {reste > 0 ? <WarningOutlined /> : <FileProtectOutlined />}
                            </div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Reste à payer</div>
                                <div className="detail-kpi-value">{formatMontant(reste)}</div>
                                <div className="detail-kpi-sub">{reste > 0 ? 'à recouvrer' : 'soldé ✓'}</div>
                            </div>
                        </div>

                        <div className="detail-kpi detail-kpi-purple">
                            <div className="detail-kpi-icon"><CalendarOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Échéance</div>
                                <div className="detail-kpi-value">{formatDate(contrat.dateEcheance)}</div>
                                <div className="detail-kpi-sub">{contrat.duree || 12} mois</div>
                            </div>
                        </div>
                    </section>

                    {/* Onglets + actions à droite */}
                    <Card className="detail-contrat-tabs-card">
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={tabItems}
                            size="middle"
                            tabBarExtraContent={{
                                right: (
                                    <div className="tabs-actions">
                                        {reste > 0 && contrat.statut === 'actif' ? (
                                            <Button
                                                type="primary"
                                                icon={<DollarOutlined />}
                                                onClick={openEncaisserModal}
                                                className="tabs-btn-encaisser"
                                            >
                                                Encaisser
                                            </Button>
                                        ) : (
                                            <Button
                                                icon={<ReloadOutlined />}
                                                onClick={() => navigate(`/dashboard/nouveau-contrat?renew=${contrat._id}`)}
                                                className="tabs-btn-renew"
                                            >
                                                Renouveler
                                            </Button>
                                        )}
                                        <Button
                                            icon={<WarningOutlined />}
                                            onClick={() => navigate(`/dashboard/nouveau-sinistre?contrat=${contrat._id}`)}
                                            className="tabs-btn-sinistre"
                                        >
                                            Sinistre
                                        </Button>
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={() => navigate(`/dashboard/nouveau-contrat?edit=${contrat._id}`)}
                                            className="tabs-btn-edit"
                                        >
                                            Modifier
                                        </Button>
                                        <Button
                                            icon={<PrinterOutlined />}
                                            onClick={() => window.print()}
                                            className="tabs-btn-print"
                                        >
                                            Imprimer
                                        </Button>
                                        <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                                            <Button
                                                icon={<DownloadOutlined />}
                                                className="tabs-btn-export"
                                            >
                                                Exporter
                                            </Button>
                                        </Dropdown>
                                    </div>
                                ),
                            }}
                        />
                    </Card>
                </div>
            </div>

            {/* Modal Encaissement */}
            <Modal
                title="Nouveau règlement"
                open={reglementModalVisible}
                onCancel={() => { setReglementModalVisible(false); reglementForm.resetFields(); }}
                footer={null}
                destroyOnClose
                width={520}
            >
                <Card size="small" style={{ background: '#f6f8fa', borderRadius: 8, marginBottom: 16 }}>
                    <Descriptions column={2} size="small">
                        <Descriptions.Item label="Client"><strong>{clientNom}</strong></Descriptions.Item>
                        <Descriptions.Item label="N° Police">
                            <span className="detail-mono">{contrat.numeroPolice}</span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Prime TTC">
                            <strong style={{ color: '#1A2B5F' }}>{formatMontant(contrat.primeTTC)}</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Reste à payer">
                            <strong style={{ color: reste > 0 ? '#ff4d4f' : '#52c41a' }}>{formatMontant(reste)}</strong>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
                <Collapse
                    ghost
                    items={[{
                        key: 'help',
                        label: (
                            <span style={{ color: '#1890ff', fontSize: 12 }}>
                                <InfoCircleOutlined style={{ marginRight: 6 }} />
                                Comment enregistrer un versement ?
                            </span>
                        ),
                        children: (
                            <Alert
                                type="info"
                                showIcon={false}
                                style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 8 }}
                                message={
                                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.7 }}>
                                        <li><strong>Montant</strong> : pré-rempli avec le reste à payer.</li>
                                        <li><strong>Mode</strong> : Espèces / Chèque / Virement / Mobile Money.</li>
                                        <li><strong>Référence</strong> : n° chèque, transaction Mobile Money, ou virement bancaire.</li>
                                        <li><strong>Trop-perçu</strong> : confirmation demandée si montant {'>'} reste dû.</li>
                                    </ul>
                                }
                            />
                        ),
                    }]}
                    style={{ marginBottom: 16 }}
                />
                <Form form={reglementForm} layout="vertical" onFinish={handleAddReglement}>
                    <Form.Item
                        name="montant"
                        label="Montant du versement"
                        rules={[{ required: true, message: 'Le montant est obligatoire' }]}
                    >
                        <MontantInput ref={reglementMontantRef} placeholder="Montant en FCFA" />
                    </Form.Item>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="moyenPaiement"
                                label="Mode de paiement"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <Select placeholder="Sélectionner">
                                    <Select.Option value="especes">Espèces</Select.Option>
                                    <Select.Option value="cheque">Chèque</Select.Option>
                                    <Select.Option value="virement">Virement</Select.Option>
                                    <Select.Option value="mobile_money">Mobile Money</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="dateReglement"
                                label="Date"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <DatePicker style={{ width: '100%', borderRadius: 8 }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="reference"
                        label="Référence"
                        normalize={(v) => v ? v.toUpperCase() : v}
                    >
                        <Input
                            placeholder="N. cheque, reference virement..."
                            style={{ borderRadius: 8, textTransform: 'uppercase' }}
                        />
                    </Form.Item>
                    <Form.Item name="observation" label="Observation">
                        <Input.TextArea
                            placeholder="Note ou commentaire"
                            rows={2}
                            style={{ borderRadius: 8 }}
                        />
                    </Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={reglementLoading}
                        block
                        style={{ borderRadius: 8, height: 44, background: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Enregistrer le règlement
                    </Button>
                </Form>
            </Modal>

            <PrintFooter />
        </div>
    );
};

export default DetailContrat;
